/**
 * Domain types shared across the admin, dashboard and demo features.
 *
 * These mirror the automations backend's API shapes. Several endpoints are
 * not implemented server-side yet (see docs in api.ts) — their response
 * types are still declared here so the UI can be built against the real
 * contract ahead of the backend landing.
 */

/**
 * Exactly the database's values, in the database's casing. These cross the wire
 * verbatim in both directions, so a friendlier-looking union here means every
 * save is rejected — which is what happened. Display wording lives below.
 */
export type ClientStatus = 'ACTIVE' | 'PAUSED' | 'OFFBOARDED';

export const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  OFFBOARDED: 'Offboarded',
};

/** Also verbatim from the database. */
export type CarePlan = 'NONE' | 'ESSENTIALS' | 'STANDARD' | 'PREMIUM';

export const CARE_PLAN_LABEL: Record<CarePlan, string> = {
  NONE: 'No plan',
  ESSENTIALS: 'Essentials',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
};

export interface Client {
  id: string;
  name: string;
  status: ClientStatus;
  carePlan: CarePlan;
  deploymentCount: number;
  createdAt: string;
}

export interface CreateClientInput {
  name: string;
}

/** All fields optional — a PATCH sends only what the admin actually edited. */
export interface UpdateClientInput {
  name?: string;
  status?: ClientStatus;
  carePlan?: CarePlan;
  notes?: string;
}

export interface ClientDetail extends Client {
  contactEmail?: string;
  notes?: string;
  /** Embedded by the detail endpoint — no separate list call is needed. */
  deployments?: Deployment[];
}

/** A single ordered field definition driving ConfigForm's schema-driven rendering. */
export interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'email' | 'url' | 'number' | 'textarea' | 'textarea[]' | 'boolean';
  required?: boolean;
  default?: string | number | boolean | string[];
  /** number: minimum value. textarea[]: minimum number of entries. */
  min?: number;
  /** number: maximum value. textarea[]: maximum number of entries. */
  max?: number;
  help?: string;
}

export interface Block {
  id: string;
  name: string;
  description: string;
  fields: FieldDef[];
}

/** Matches the database exactly — the API returns these values verbatim. */
export type DeploymentStatus = 'DRAFT' | 'DEPLOYING' | 'ENABLED' | 'DISABLED' | 'ERROR';

export interface Deployment {
  id: string;
  clientId: string;
  blockId: string;
  blockName: string;
  outcomeNoun?: string;
  status: DeploymentStatus;
  config: Record<string, unknown>;
  createdAt: string;
  deployedAt?: string | null;
  statusDetail?: string | null;
  /**
   * The flow behind this record no longer exists in the engine — usually
   * because it was deleted there by hand. The portal still has the record, so
   * it is shown as broken rather than as a working automation.
   */
  missingInEngine?: boolean;
  /**
   * A record with no automation behind it — the seeded demo client's blocks,
   * which exist so its page agrees with its ledger history. Shown as normal
   * but not operable; the API refuses anything that would reach the engine.
   */
  seededDemo?: boolean;
}

export interface DeployBlockInput {
  blockId: string;
  config: Record<string, unknown>;
}

export type HealthStatus = 'ok' | 'degraded' | 'down';

export interface HealthReport {
  status: HealthStatus;
  checks: { name: string; status: HealthStatus; detail?: string }[];
  checkedAt: string;
}

export type PortalRole = 'admin' | 'client';

export interface ClientMe {
  clientId: string;
  clientName: string;
  role: PortalRole;
}

export interface OutcomesSummary {
  runsThisMonth: number;
  hoursSaved: number;
  activeBlocks: number;
}

/** A single canned scenario belonging to a demo block. */
export interface DemoScenario {
  id: string;
  title: string;
  inputText: string;
}

/** A demo-provisioned automation, as returned by GET /api/demo/scenarios. */
export interface DemoBlock {
  blockId: string;
  name: string;
  description: string;
  scenarios: DemoScenario[];
}

export type DemoRunStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED';

export interface DemoRun {
  id: string;
  blockId: string;
  status: DemoRunStatus;
  outputText: string | null;
}

export interface StartDemoRunInput {
  blockId: string;
  // Exactly one of these is sent: a canned scenario, or the visitor's own
  // text. `| undefined` is required under exactOptionalPropertyTypes so the
  // caller can pass the unused one through explicitly.
  scenarioId?: string | undefined;
  input?: string | undefined;
}

export type LedgerSeverity = 'INFO' | 'ACTION' | 'MONEY' | 'ERROR';

export type LedgerApprovalState = 'PENDING' | 'APPROVED' | 'HELD' | 'EXPIRED';

/** One row per client on the ledger overview — a rollup, not the events themselves. */
export interface OverviewRow {
  clientId: string;
  name: string;
  status: string;
  carePlan: string;
  blocksEnabled: number;
  blocksTotal: number;
  blockNames: string[];
  lastEventAt: string | null;
  lastEventSummary: string | null;
  openApprovals: number;
  errors: number;
  eventsThisWeek: number;
}

export interface LedgerEvent {
  id: string;
  occurredAt: string;
  recordedAt: string;
  clientId: string;
  blockId: string;
  activity: string;
  threadKey: string;
  summary: string;
  valuePence: number | null;
  minutesSaved: number | null;
  needsApproval: boolean;
  approvalState: LedgerApprovalState | null;
  severity: LedgerSeverity;
  payload: unknown;
}

/** A run of related events for one client + block, e.g. everything tied to a single invoice. */
export interface LedgerThread {
  threadKey: string;
  blockId: string;
  startedAt: string;
  lastEventAt: string;
  currentActivity: string;
  currentSummary: string;
  openApprovals: number;
  errors: number;
  valuePence: number;
  minutesSaved: number;
  events: LedgerEvent[];
}

export interface LedgerEventWithClient extends LedgerEvent {
  client: { id: string; name: string };
}

export interface LedgerDelivery {
  id: string;
  channel: string;
  recipient: string;
  sentAt: string | null;
  providerStatus: string | null;
  error: string | null;
  createdAt: string;
  event: { id: string; activity: string; summary: string; threadKey: string; client: { id: string; name: string } };
}

/** Severity drives the colour of a timeline marker. */
export type EventSeverity = 'INFO' | 'ACTION' | 'MONEY' | 'ERROR';
export type ApprovalState = 'PENDING' | 'APPROVED' | 'HELD' | 'EXPIRED';

/**
 * The address a client forwards their work to.
 *
 * `address` is the whole thing, ready to hand over — the portal never rebuilds
 * it from the local part, because the domain lives in the API's config and two
 * places holding it is how they end up disagreeing.
 */
export interface InboundAddress {
  id: string;
  localpart: string;
  address: string;
  deploymentId: string;
  blockName?: string;
  active: boolean;
  createdAt?: string;
}
